import React from 'react';
import { toast } from '~/utils/toast';

import { CheckIcon } from '@heroicons/react/24/outline';

import Modal, { useModal } from '../Modal';
import TextInput from '../TextInput';
import Button from '../Button';
import Tooltip from '../Tooltip';
import { api } from '~/utils/api';
import { StatusOptions } from '~/config';
import dayjs from 'dayjs';

type ConfirmClaimModalProps = {
  claimId: string;
  reloadDoc: () => Promise<void>;
};

export default function ConfirmClaimModal({ claimId, reloadDoc }: ConfirmClaimModalProps) {
  const { modalRef, openModal, closeModal } = useModal();
  const [approval, setApproval] = React.useState<'LIMITED_APPROVAL' | 'STANDING_APPROVAL' | null>(
    null,
  );
  const [expirationDate, setExpirationDate] = React.useState<string | null>(null);
  const [disclosure, setDisclosure] = React.useState<string | undefined>();
  const [limitations, setLimitations] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState(false);
  const { mutateAsync: actionPolicyFeedback } = api.feedback.actionPolicyFeedback.useMutation();
  return (
    <>
      <Tooltip content="Confirm" hideIcon>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Confirm claim"
          onClick={(e) => {
            e.stopPropagation();
            openModal();
          }}
        >
          <CheckIcon className="size-4" strokeWidth={2} />
        </Button>
      </Tooltip>
      <Modal ref={modalRef} header={'Confirm Claim'}>
        <div className="mt-3 text-sm">
          <label className="flex cursor-pointer items-center gap-2 py-1.5">
            <input
              type="radio"
              name={`confirm-claim-${claimId}`}
              className="border-input size-3.5 shrink-0 accent-foreground"
              onChange={(e) => {
                setApproval(e.target.checked ? 'LIMITED_APPROVAL' : null);
              }}
            />
            <span className="text-sm font-medium">Limited Approval</span>
          </label>
          {approval === 'LIMITED_APPROVAL' && (
            <>
              <TextInput
                placeholder="Enter limitations"
                label=""
                className="mb-2"
                value={limitations}
                onChange={(e) => setLimitations(e.target.value)}
              />
              <TextInput
                inputProps={{
                  type: 'date',
                  min: dayjs().format('YYYY-DD-MM'),
                }}
                onChange={(e) => {
                  setExpirationDate(e.target.value);
                }}
                label="Expiration Date of Approval"
              />
            </>
          )}
          <label className="flex cursor-pointer items-center gap-2 py-1.5">
            <input
              type="radio"
              name={`confirm-claim-${claimId}`}
              className="border-input size-3.5 shrink-0 accent-foreground"
              onChange={(e) => {
                setApproval(e.target.checked ? 'STANDING_APPROVAL' : null);
              }}
            />
            <span className="text-sm font-medium">Standing Approval</span>
          </label>

          <TextInput
            inputProps={{ role: 'button', tabIndex: 0 }}
            placeholder="Text required for this claim"
            label="Claim Disclosure"
            className="mb-2"
            value={disclosure}
            onChange={(e) => {
              setDisclosure(e.target.value);
            }}
          />
          <Button
            type="button"
            label="Submit"
            size="sm"
            className="mt-3"
            fullWidth
            onClick={async () => {
              if (!approval) return;
              try {
                setIsLoading(true);
                await actionPolicyFeedback({
                  claim: {
                    id: claimId,
                    approvalType: approval,
                    status: StatusOptions.APPROVED,
                    disclosureText: disclosure,
                    approvalLimitations: approval === 'LIMITED_APPROVAL' ? limitations : undefined,
                    approvalExpireAt: expirationDate
                      ? new Date(expirationDate).toISOString()
                      : undefined,
                  },
                });
                await reloadDoc();
                toast.success('Claim has been approved');
                closeModal();
              } catch (e) {
                const msg = 'Failed to approve claim';
                console.error(msg, e);
                toast.error(msg);
              } finally {
                setIsLoading(false);
              }
            }}
            isLoading={isLoading}
            disabled={!approval}
          />
        </div>
      </Modal>
    </>
  );
}
